import * as React from 'react'
import { observer, Provider } from 'mobx-react'
import { LoadingAnimation } from '../components/LoadingAnimation'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../stores/CurrentUserStore'
import { message } from 'antd-v4'
import * as auth from '../utilities/auth'
import { parseErrorMsg } from '../utilities/helpers'
import { when } from 'mobx'
import { GlobalStore } from '../stores/GlobalStore'

interface Props {
  params: any
}

@observer
export class CompanyRoot extends React.Component<React.PropsWithChildren<Props>, {}> {
  currentUserStore = new CurrentUserStore()
  currentCompanyStore = new CurrentCompanyStore()

  componentDidMount = async () => {
    const authToken = window.localStorage.getItem('authToken')

    if (authToken !== null) {
      try {
        await auth.setupInterceptor()
        await this.currentUserStore.getCurrentHumanUser()
      } catch (error) {
        window.location.href = '/'
      }
    }

    try {
      await this.currentCompanyStore.getById(this.props.params.companyId)
      await this.currentCompanyStore.getLocations()
      await this.currentCompanyStore.getTags()
      if (!this.currentCompanyStore.globalLocations) {
        await this.currentCompanyStore.getGlobalLocations()
      }

      await auth.setupInterceptor()
    } catch (error: any) {
      if (error && error.response && [400, 401, 403, 404].includes(error.response.status)) {
        window.location.href = '/'
      }
    }

    try {
      /* Feature Toggles */
      await this.currentCompanyStore.getCanUseCustomerSearchV2()
      await this.currentCompanyStore.getCanUseInsightsQuicksight(
        this.currentUserStore.currentUser?.id,
      )
      await this.currentCompanyStore.getCanUseLoyaltyService()
      await this.currentCompanyStore.getExternalLoyaltyProvider()
      await this.currentCompanyStore.getWorldpayStatus()

      await this.currentCompanyStore.getProductDetailsPackageDateStatus()
      await this.currentCompanyStore.getCanUseOCSComplianceStatus()
      await this.currentCompanyStore.getCanDisplayLoyaltyInfoOnReceipt()
      await this.currentCompanyStore.getCanBulkCreateProducts()
      await this.currentCompanyStore.getCanUseBulkProductUpdate()
      await this.currentCompanyStore.getCanUsePlatformCustomersSearch()
      await this.currentCompanyStore.getCanUsePlatformCustomersSearchOutreach()
      await this.currentCompanyStore.getCanUseAutoLoyaltyDiscount()
      await this.currentCompanyStore.getCanViewInventorySnapshots()
      await this.currentCompanyStore.getCanUseGlobalCatalogStatus()
      await this.currentCompanyStore.getCanUseOverlappingAutoDiscounts()
      await this.currentCompanyStore.getCanManageStackingDiscounts()
      await this.currentCompanyStore.getCanUseTaxConfig()
      await this.currentCompanyStore.getCanUsePricingMenus()
      await this.currentCompanyStore.getCanUseSingleBarcodeValidation()

      await this.currentCompanyStore.getCanUseMultiProvDashboard(
        this.currentUserStore.currentUser?.id,
      )

      // If this is a Blaze app, we need to check if the company can use external payment terminals as exception.
      //  This is a feature toggle that is only relevant for Blaze apps.
      if (GlobalStore.isBlazeApp) {
        await this.currentCompanyStore.getCanUseExternalPaymentTerminals()
      }

      /* Data Loaders */
      if (this?.currentCompanyStore?.canUseOCSCompliance) {
        await this.currentCompanyStore.getGlobalHashKeys()
      }

      // If parentEntityId exists and is the same as rootEntityId, then this company is an organization company.
      const { parentEntityId, rootEntityId } = this.currentCompanyStore.company || {}
      const isOrganizationCompany = Boolean(parentEntityId && parentEntityId === rootEntityId)

      if (
        this.currentCompanyStore.canUseMultiProvDashboard &&
        isOrganizationCompany &&
        parentEntityId
      ) {
        await this.currentCompanyStore.loadOrganizationData()
        await when(() => !this.currentCompanyStore.isLoadingOrgData)
        await this.currentCompanyStore.getOrgLocationResponse(parentEntityId).catch((error) => {
          if (error && error.response && error.response.status === 403) {
            // If the user doesn't have access to ALL org locations, we can still allow them to use the multi-prov dashboard features with the locations they do have access to. So we can ignore 403 errors here.
            return
          }

          throw error
        })
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading feature state')
    }
  }

  render() {
    const companyLoaded = this.currentCompanyStore.company !== undefined
    const userLoaded = this.currentUserStore.currentUser !== undefined
    const locationsLoaded = this.currentCompanyStore.locations !== undefined

    if (!userLoaded || !companyLoaded || !locationsLoaded) {
      return (
        <div className='full-height'>
          <div className='container'>
            <LoadingAnimation />
          </div>
        </div>
      )
    } else {
      return (
        <Provider
          currentUserStore={this.currentUserStore}
          currentCompanyStore={this.currentCompanyStore}
        >
          <div>{this.props.children}</div>
        </Provider>
      )
    }
  }
}
